import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ToastComponent } from '@tylertech/forge';
import { createRandomID } from '../reusable/reusableFunctions.js';
import { sendChatFeedback } from '../../services/ragService.js';
import { generateMessage, PROMPT_TYPES } from './pbbStrategistHelpers.js';
import { messageManager } from '../System/messageManager/messageManager.js';

export const ChatActionsContext = createContext(null);

export const ChatActionsProvider = ({ children }) => {
  const [waitingResponse, setWaitingResponse] = useState(false);
  const chatbotRef = useRef(null);

  const onSendMessage = async (question, type, context, modalData) => {
    const chatbotEl = chatbotRef.current;
    const adapter = chatbotEl?.adapter;
    if (!chatbotEl || !adapter) return;

    const prompt = generateMessage(question, type, modalData);
    if (typeof adapter.setPromptContext === 'function') adapter.setPromptContext(type, context || {});

    try {
      const text = typeof prompt === 'string' ? prompt : JSON.stringify(prompt);
      await chatbotEl.sendMessage(text, []);
    } catch (error) {
      setWaitingResponse(false);
    }
  };

  const onMessageFeedback = useCallback((action, feedback, id) => {
    sendChatFeedback({ reactionType: action, feedbackText: feedback, messageId: id });
  }, []);

  const onSubmitProgramDossier = (data) => {
    const context = { program_name: data.programName, department_name: data.departmentName };
    onSendMessage(null, PROMPT_TYPES.PROGRAM_DOSSIER, context, data);
  };

  const onSubmitSavings = (data) => {
    const context = { amount: data.amount, department_name: data.departmentName };
    onSendMessage(null, PROMPT_TYPES.FIND_SAVINGS, context, data);
  };

  const onSubmitQuickWins = (data) => {
    const context = {
      department: data.departmentName,
      top_wins: data.topWins,
      period_of_time: data.periodOfTime,
      staff_impact_tolerance: data.impactTolerance,
      implementation_complexity: data.complexity,
      push_back_absorption: data.absorption,
      public_council_reception: data.reception,
      min_savings_target_types: data.opportunity,
      min_savings_target_amount: data.minimumSavingsTarget || null,
    };
    onSendMessage(null, PROMPT_TYPES.QUICK_WINS, context, data);
  };

  const onSubmitImagineWithAI = (data) => {
    onSendMessage(null, PROMPT_TYPES.REIMAGINE_WITH_AI, data, data);
  };

  const onConversationReset = () => {
    const chatbotEl = chatbotRef.current;
    if (!chatbotEl) return;

    if (chatbotEl?.adapter?.isRunning) chatbotEl.adapter.abort();

    if (typeof chatbotEl?.startNewChat === 'function') chatbotEl.startNewChat();
    else if (typeof chatbotEl?.clearMessages === 'function') chatbotEl.clearMessages();

    setWaitingResponse(false);
    if (chatbotEl.adapter) chatbotEl.adapter.threadId = createRandomID();
  };

  const onCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      ToastComponent.present({
        message: messageManager().PBB_STRATEGIST.FAILED_TO_COPY_CLIPBOARD,
      });
    }
  };

  return (
    <ChatActionsContext.Provider
      value={{
        onSendMessage,
        onMessageFeedback,
        waitingResponse,
        setWaitingResponse,
        onConversationReset,
        onSubmitProgramDossier,
        onSubmitSavings,
        onSubmitQuickWins,
        onSubmitImagineWithAI,
        chatbotRef,
        onCopy,
      }}
    >
      {children}
    </ChatActionsContext.Provider>
  );
};

ChatActionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useChatActions = () => {
  const context = useContext(ChatActionsContext);
  if (!context) throw new Error('useChatActions must be used inside ChatActionsProvider');
  return context;
};
